/**
 * Environment Variable Validation
 * Validates required environment variables at application startup
 */

/* eslint-disable no-console */
/* eslint-disable no-alert */

const requiredEnvVars = ['REACT_APP_PROJECT_ID', 'REACT_APP_BASE_URL'];

const optionalEnvVars = [
  'REACT_APP_AES_SECRET',
  'REACT_APP_AES_VECTOR',
  'REACT_APP_API_SSO_BASE_URL',
  'REACT_APP_BY_PASS_KEY',
  'REACT_APP_BASE_USA_URL',
  'REACT_APP_GA_TRACKING_ID',
  'REACT_APP_GA_MEASUREMENT_ID',
];

/**
 * Validates required environment variables
 * @throws {Error} If any required environment variable is missing
 */
export function validateEnv() {
  const missing = requiredEnvVars.filter((varName) => {
    const value = process.env[varName];
    return !value || value.trim() === '';
  });

  if (missing.length > 0) {
    const errorMessage =
      `Missing required environment variables: ${missing.join(', ')}\n\n` +
      `Please ensure these variables are set in your .env file or environment.\n` +
      `See .env.example for reference.`;

    console.error('❌ Environment Validation Failed');
    console.error(errorMessage);

    // In development, show alert for better visibility
    if (process.env.NODE_ENV === 'development') {
      alert(`Environment Configuration Error:\n\n${errorMessage}`);
    }

    throw new Error(errorMessage);
  }

  // Warn about missing optional but recommended variables
  const missingOptional = optionalEnvVars.filter((varName) => {
    const value = process.env[varName];
    return !value || value.trim() === '';
  });

  if (missingOptional.length > 0 && process.env.NODE_ENV === 'development') {
    console.warn(
      '⚠️  Missing optional environment variables:',
      missingOptional.join(', ')
    );
    console.warn(
      'Some features may not work correctly without these variables.'
    );
  }

  console.log('✅ Environment variables validated successfully');
}

/**
 * Gets an environment variable value
 * @param {string} varName - Environment variable name
 * @param {string} defaultValue - Default value if not set
 * @returns {string} Environment variable value or default
 */
export function getEnvVar(varName, defaultValue = '') {
  return process.env[varName] || defaultValue;
}

/**
 * Checks if an environment variable is set
 * @param {string} varName - Environment variable name
 * @returns {boolean} True if variable is set and not empty
 */
export function hasEnvVar(varName) {
  const value = process.env[varName];
  return !!(value && value.trim() !== '');
}
